import { Button, Image, Input, Space } from "antd";
import { useEffect, useState } from "react";
import UserImg from "../../assets/images/avatar.svg";
import LinkIcon from "../../assets/images/external-link.png";
import PDF from "../../assets/images/pdf.svg";
import {
  useAddCommentMutation,
  useGetCommentQuery,
  useGetSupportDetailsQuery,
  useSupportCloseMutation,
} from "../../features/supportTicket/supportTicketApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { formatDate, formatTime } from "../../lib/time";
import { toast } from "../../lib/toast";
import { ModalComponentProps } from "../../types";
import { capitalize, getExtension } from "../../utils/string";
import InfoItem from "../common/InfoItem";
import ConfirmModal from "../ui/ConfirmModal";
import CustomModal from "../ui/CustomModal";
import Loader from "../ui/Loader";
import moment from "moment";
import IoIosSendIcon from "../../assets/icons/IoIosSendIcon";

enum Status {
  open = "status-danger",
  closed = "status-success",
}

export default function RequestDetailsModal({
  handleClose,
  data: id,
  ...rest
}: ModalComponentProps<string>) {
  const [comment, setComment] = useState<undefined | string>(undefined);
  const {
    open,
    handleOpen,
    handleClose: handleRequestClose,
  } = useModal<undefined, "REQUEST_CLOSE">();
  const { isLoading, isError, error, data } = useGetSupportDetailsQuery(
    id || "",
    {
      skip: !id,
    }
  );
  const [
    supportClose,
    {
      isLoading: isSupportLoading,
      isError: isSupportError,
      error: supportData,
      isSuccess: isRequestSuccess,
    },
  ] = useSupportCloseMutation();
  const {
    isLoading: isCommentLoading,
    isError: isCommentError,
    error: commentError,
    data: commentData,
  } = useGetCommentQuery(id || "", {
    skip: !id,
  });
  const [
    addComment,
    {
      isLoading: isAddCommentLoading,
      isError: isAddCommentError,
      error: addCommentData,
      isSuccess: isAddCommentSuccess,
    },
  ] = useAddCommentMutation();
  useError(isError, error, "Get support details data fail");
  useError(isSupportError, supportData, "Request close fail");
  useError(isCommentError, commentError, "Get comment data fail");
  useError(isAddCommentError, addCommentData, "Add comment data fail");

  const details = data?.data?.support;

  // success toast message
  useEffect(() => {
    if (isRequestSuccess) {
      toast("success", "Successfully Closed!");
      handleRequestClose();
      handleClose();
    }
  }, [handleClose, handleRequestClose, isRequestSuccess]);

  const handleComment = () => {
    if (!comment) return;

    addComment({
      support_id: Number(id),
      comment: comment,
    });
  };

  useEffect(() => {
    if (isAddCommentSuccess) {
      // toast("success", "Comment added successfully!");
      setComment(undefined);
    }
  }, [isAddCommentSuccess]);

  return (
    <>
      <CustomModal
        title="Ticket Details"
        {...rest}
        handleClose={handleClose}
        width={600}
        footer={null}
        closable={!isLoading}
      >
        {open?.type === "REQUEST_CLOSE" && (
          <ConfirmModal
            open={open?.open}
            okLoading={isSupportLoading}
            handleOk={() => supportClose(id || "")}
            handleClose={handleRequestClose}
            confirmTitle="Request close"
            questionText="Do you want to close the support ticket?"
            okText="Confirm"
          />
        )}
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <InfoItem title="ID" className="font-semibold">
              {details?.id}
            </InfoItem>
            <InfoItem title="Status">
              <span className={details?.status ? Status[details.status] : ""}>
                {details?.status ? capitalize(details?.status) : undefined}
              </span>
            </InfoItem>
            <InfoItem title="Title">{details?.title || "n/a"}</InfoItem>
            <InfoItem title="Details (Support)">
              {details?.long_description}
            </InfoItem>
            <InfoItem title="Requested at">
              {formatTime(details?.created_at, "hh:mm A")}{" "}
              {formatDate(details?.created_at)}
            </InfoItem>
            <InfoItem title="Assignee">
              {details?.assign_to_info ? (
                <>
                  {" "}
                  <h6 className="font-medium">
                    {details?.assign_to_info?.name}
                  </h6>
                  <span className="font-normal">
                    ID: {details?.assign_to_info?.employee_id || "-"}
                  </span>
                </>
              ) : (
                "n/a"
              )}
            </InfoItem>
            <InfoItem title="Requested by" rootClassName="border-b-0">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10">
                  <img
                    className="w-full h-full rounded-full"
                    src={details?.member_info?.pro_pic || UserImg}
                    alt=""
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-body text-base font-medium">
                    {details?.member_info?.name}
                  </h3>

                  <p className="text-display-body text-sm font-medium">
                    {details?.member_info?.designation}
                  </p>
                </div>
              </div>
            </InfoItem>
            <InfoItem title="Attachment">
              {details?.attachment ? (
                <div className="flex items-end gap-2">
                  <Image
                    src={
                      getExtension(details?.attachment) === "pdf"
                        ? PDF
                        : details?.attachment
                    }
                    width={50}
                    height={40}
                    className={
                      getExtension(details?.attachment) === "pdf"
                        ? "object-contain"
                        : "object-cover"
                    }
                    preview={getExtension(details?.attachment) !== "pdf"}
                  />
                  <span className="text-accent flex items-center gap-1 text-xs mt-1">
                    <a
                      href={details?.attachment}
                      target="_blank"
                      className="-mt-[2px] cursor-pointer"
                      rel="noreferrer"
                      download="Attachment"
                    >
                      <img className="size-3" src={LinkIcon} alt="" />
                    </a>{" "}
                  </span>
                </div>
              ) : null}
            </InfoItem>

            <div className="mt-4 flex flex-col gap-4">
              {isCommentLoading ? (
                <Loader />
              ) : (
                <>
                  {commentData?.data?.comments?.map((comment) => (
                    <CommentItem
                      name={comment?.commenter?.member?.profile?.name}
                      comment={comment?.comment}
                      time={comment?.created_at}
                      img={comment?.commenter?.member?.profile?.pro_pic}
                    />
                  ))}

                  {details?.status === "open" ? (
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Add Comment"
                        size="large"
                        className="flex-1"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                      <Button
                        size="large"
                        className="min-w-[100px]"
                        type="primary"
                        loading={isAddCommentLoading}
                        disabled={!comment}
                        onClick={handleComment}
                      >
                        Send
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <Space className="w-full mt-6" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={handleClose}
                size="middle"
              >
                Cancel
              </Button>
              {details?.status === "open" ? (
                <Button
                  className="modal-button"
                  type="primary"
                  size="middle"
                  onClick={() =>
                    handleOpen({
                      type: "REQUEST_CLOSE",
                    })
                  }
                  // onClick={handleClose}
                >
                  Close
                </Button>
              ) : null}
            </Space>
          </>
        )}
      </CustomModal>
    </>
  );
}

type CommentItemType = {
  img?: string;
  name: string;
  comment: string;
  time: string;
};

const CommentItem = ({
  img = UserImg,
  name,
  comment,
  time,
}: CommentItemType) => {
  return (
    <div className="flex gap-3">
      <div className="w-12">
        <img className="w-12 h-12 rounded-full object-cover" src={img} alt="" />
      </div>

      <div className="flex-1">
        <h3 className="text-body text-base font-medium flex items-center gap-2 flex-wrap">
          {name}

          <span className="!text-xs text-display-dark font-normal">
            {moment(time).fromNow()}
          </span>
        </h3>
        <p className="text-display-body !text-sm">{comment}</p>
      </div>
    </div>
  );
};
